import { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave } from 'react-icons/fa';
import api from '../../api';
import { useColumnVisibility, ColumnPicker } from '../../components/ColumnPicker';

const TYPES   = ['Contract', 'Contract-to-Hire', 'Full-time'];
const REMOTES = ['Remote', 'Hybrid', 'Onsite'];

const empty = { title: '', type: 'Contract', location: '', remote: 'Hybrid', duration: '', tech: '', description: '', category: '' };

export default function AdminPositions() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const load = () => {
    setLoading(true);
    api.getPositions()
      .then(data => setPositions(data.map(p => ({ ...p, tech: Array.isArray(p.tech) ? p.tech : JSON.parse(p.tech || '[]') }))))
      .catch(() => setError('Failed to load positions.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openNew = () => { setForm(empty); setEditingId(null); setShowForm(true); setError(''); };

  const openEdit = (p) => {
    setForm({ ...p, tech: Array.isArray(p.tech) ? p.tech.join(', ') : p.tech });
    setEditingId(p.id);
    setShowForm(true);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      tech: form.tech.split(',').map(t => t.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await api.updatePosition(editingId, payload);
      } else {
        await api.createPosition(payload);
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this position?')) return;
    try {
      await api.deletePosition(id);
      load();
    } catch {
      setError('Failed to delete.');
    }
  };

  const POS_COLS = [{ key: 'type', label: 'Type' }, { key: 'location', label: 'Location' }, { key: 'remote', label: 'Remote' }];
  const { visible: posCols, toggle: togglePosCol, show: showPosCol } = useColumnVisibility(POS_COLS, 'cols_positions');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#0c1b4d]">Manage Open Positions</h1>
            <p className="text-gray-500 text-sm mt-1">Add, edit or remove job listings on the Careers page.</p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors"
          >
            <FaPlus size={12} /> Add Position
          </button>
        </div>

        {error && <p className="text-red-600 text-sm mb-4 bg-red-50 px-4 py-2 rounded-lg">{error}</p>}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="font-bold text-[#0c1b4d]">{editingId ? 'Edit Position' : 'New Position'}</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Job Title *</label>
                  <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                    placeholder="e.g. React Developer"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Type *</label>
                    <select required value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Remote *</label>
                    <select required value={form.remote} onChange={e => setForm({...form, remote: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {REMOTES.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Location *</label>
                    <input required value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                      placeholder="e.g. Boston, MA"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Duration</label>
                    <input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})}
                      placeholder="e.g. 6 months"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Technologies <span className="font-normal text-gray-400">(comma separated)</span></label>
                  <input value={form.tech} onChange={e => setForm({...form, tech: e.target.value})}
                    placeholder="e.g. React, TypeScript, Tailwind CSS"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Category <span className="font-normal text-gray-400">(used for filtering)</span></label>
                  <input value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                    placeholder="e.g. React"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                  <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                    placeholder="Brief description of the role..."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                {error && <p className="text-red-600 text-xs">{error}</p>}
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-60">
                    <FaSave size={12} /> {saving ? 'Saving...' : 'Save Position'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : positions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <p className="text-gray-400 text-sm mb-4">No positions yet. Add your first one!</p>
            <button onClick={openNew} className="px-4 py-2 bg-[#0c1b4d] text-white text-sm rounded-xl hover:bg-blue-900">
              Add Position
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100">
            <ColumnPicker columns={POS_COLS} visible={posCols} onToggle={togglePosCol} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Title</th>
                    {showPosCol('type')     && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Type</th>}
                    {showPosCol('location') && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Location</th>}
                    {showPosCol('remote')   && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Remote</th>}
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map(p => (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-[#0c1b4d] whitespace-nowrap">{p.title}</td>
                      {showPosCol('type')     && <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{p.type}</td>}
                      {showPosCol('location') && <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{p.location}</td>}
                      {showPosCol('remote')   && <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{p.remote}</td>}
                      <td className="px-5 py-3">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => openEdit(p)} className="p-2 text-gray-400 hover:text-[#0c1b4d] hover:bg-blue-50 rounded-lg transition-colors"><FaEdit size={13} /></button>
                          <button onClick={() => handleDelete(p.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><FaTrash size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
